/**
 * @fileoverview Game Status Bar Component
 * @description Displays current turn, player info, and game status
 */

import React from "react";
import { View, Text, Image, StyleSheet } from "react-native";
import { PlayerSymbol, GameStatus } from "../types";

interface GameStatusBarProps {
  currentTurn: PlayerSymbol;
  mySymbol: PlayerSymbol | null;
  isMyTurn: boolean;
  gameStatus: GameStatus;
  winner: PlayerSymbol | null;
  opponentInfo: {
    id: string;
    name: string;
    avatar_url?: string | null;
  } | null;
  myInfo: {
    name: string;
    avatar_url?: string | null;
  };
  moveCount: number;
}

/**
 * GameStatusBar - Shows game state and player information
 */
export const GameStatusBar: React.FC<GameStatusBarProps> = ({
  currentTurn,
  mySymbol,
  isMyTurn,
  gameStatus,
  winner,
  opponentInfo,
  myInfo,
  moveCount,
}) => {
  /**
   * Get status message based on game state
   */
  const getStatusMessage = (): string => {
    if (gameStatus === "waiting") {
      return "Wachten op tegenstander...";
    }

    if (gameStatus === "finished") {
      if (winner === mySymbol) {
        return "🎉 Je hebt gewonnen!";
      } else {
        return "😔 Je hebt verloren";
      }
    }

    if (gameStatus === "draw") {
      return "🤝 Gelijkspel!";
    }

    if (isMyTurn) {
      return "Jouw beurt!";
    }

    return `${opponentInfo?.name ?? "Tegenstander"} is aan de beurt`;
  };

  /**
   * Get status color
   */
  const getStatusColor = (): string => {
    if (gameStatus === "finished") {
      return winner === mySymbol ? "#22C55E" : "#EF4444";
    }
    if (gameStatus === "draw") {
      return "#F59E0B";
    }
    if (isMyTurn) {
      return "#8B5CF6";
    }
    return "#9CA3AF";
  };

  return (
    <View style={styles.container}>
      {/* Player Avatars */}
      <View style={styles.playersRow}>
        {/* Me */}
        <View style={[styles.playerCard, isMyTurn && styles.activePlayer]}>
          {myInfo.avatar_url ? (
            <Image source={{ uri: myInfo.avatar_url }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <Text style={styles.avatarText}>
                {myInfo.name?.charAt(0) ?? "?"}
              </Text>
            </View>
          )}
          <Text style={styles.playerName} numberOfLines={1}>
            {myInfo.name ?? "Jij"}
          </Text>
          <View
            style={[
              styles.symbolBadge,
              mySymbol === "X" ? styles.symbolX : styles.symbolO,
            ]}
          >
            <Text style={styles.symbolText}>{mySymbol}</Text>
          </View>
        </View>

        {/* VS */}
        <View style={styles.vsContainer}>
          <Text style={styles.vsText}>VS</Text>
          <Text style={styles.moveCountText}>{moveCount} zetten</Text>
        </View>

        {/* Opponent */}
        <View style={[styles.playerCard, !isMyTurn && gameStatus === "playing" && styles.activePlayer]}>
          {opponentInfo?.avatar_url ? (
            <Image
              source={{ uri: opponentInfo.avatar_url }}
              style={styles.avatar}
            />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <Text style={styles.avatarText}>
                {opponentInfo?.name?.charAt(0) ?? "?"}
              </Text>
            </View>
          )}
          <Text style={styles.playerName} numberOfLines={1}>
            {opponentInfo?.name ?? "Tegenstander"}
          </Text>
          <View
            style={[
              styles.symbolBadge,
              mySymbol === "X" ? styles.symbolO : styles.symbolX,
            ]}
          >
            <Text style={styles.symbolText}>
              {mySymbol === "X" ? "O" : "X"}
            </Text>
          </View>
        </View>
      </View>

      {/* Status Message */}
      <View style={[styles.statusBar, { backgroundColor: getStatusColor() }]}>
        <Text style={styles.statusText}>{getStatusMessage()}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  playersRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  playerCard: {
    flex: 1,
    alignItems: "center",
    padding: 12,
    backgroundColor: "#1F2937",
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "transparent",
  },
  activePlayer: {
    borderColor: "#8B5CF6",
    backgroundColor: "rgba(139, 92, 246, 0.1)",
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginBottom: 6,
  },
  avatarPlaceholder: {
    backgroundColor: "#374151",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  playerName: {
    fontSize: 12,
    fontWeight: "600",
    color: "#E5E7EB",
    textAlign: "center",
    maxWidth: 80,
  },
  symbolBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  symbolX: {
    backgroundColor: "#EF4444",
  },
  symbolO: {
    backgroundColor: "#3B82F6",
  },
  symbolText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  vsContainer: {
    alignItems: "center",
    paddingHorizontal: 16,
  },
  vsText: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#6B7280",
  },
  moveCountText: {
    fontSize: 10,
    color: "#9CA3AF",
    marginTop: 2,
  },
  statusBar: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  statusText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default GameStatusBar;

